import { ContactWithAuthorService } from "../services/ContactWithAuthorService";

class ContactWithAuthorController {
    private readonly root: HTMLElement;
    private readonly formEl: HTMLFormElement;
    private readonly messageEl: HTMLTextAreaElement;
    private readonly attachBtnEl: HTMLButtonElement;
    private readonly fileInputEl: HTMLInputElement;
    private readonly fileNameEl: HTMLElement;
    private readonly submitBtnEl: HTMLButtonElement;
    private readonly bodyFormEl: HTMLElement;
    private readonly bodyResultEl: HTMLElement;
    private readonly errorEl: HTMLElement;
    private readonly successEl: HTMLElement;
    
    private isSaving = false;
    private formSubmitted = false;
    private hasErrors = false;
    private file: File | null = null;
    
    private emailData: EmailData = {
        message: "",
        authorId: 0,
        contentId: 0,
        file: null,
        fileName: "",
    };
    
    constructor(root: HTMLElement) {
        this.root = root;
        this.formEl = this.q<HTMLFormElement>(".js-contact-form");
        this.messageEl = this.q<HTMLTextAreaElement>(".js-message");
        this.attachBtnEl = this.q<HTMLButtonElement>(".js-attach-btn");
        this.fileInputEl = this.q<HTMLInputElement>(".js-file-input");
        this.fileNameEl = this.q<HTMLElement>(".js-file-name");
        this.submitBtnEl = this.q<HTMLButtonElement>(".js-submit");
        this.bodyFormEl = this.q<HTMLElement>(".js-body-form");
        this.bodyResultEl = this.q<HTMLElement>(".js-body-result");
        this.errorEl = this.q<HTMLElement>(".js-error");
        this.successEl = this.q<HTMLElement>(".js-success");
        const contentIdAttr = this.root.getAttribute("data-content-id") || "0";
        const authorIdAttr = this.root.getAttribute("data-author-id") || "0";
        this.emailData.contentId = parseInt(contentIdAttr, 10) || 0;
        this.emailData.authorId = parseInt(authorIdAttr, 10) || 0;
        
        this.wireEvents();
        this.updateSubmitState();
    }
    
    private q<T extends Element = Element>(selector: string): T {
        const el = this.root.querySelector(selector);
        if (!el) {
        throw new Error(`Missing element: ${selector}`);
        }
        return el as T;
    }
    
    private wireEvents() {
        this.formEl.addEventListener("submit", (e) => {
            e.preventDefault();
            this.sendEmailToAuthor().catch(() => { /* handled in method */ });
        });
    
        this.formEl.addEventListener("input", () => {
        this.emailData.message = this.messageEl.value;
        this.updateSubmitState();
        });
    
        this.attachBtnEl.addEventListener("click", () => {
        this.fileInputEl.click();
        });
    
        this.fileInputEl.addEventListener("change", () => {
        const file = this.fileInputEl.files && this.fileInputEl.files[0] ? this.fileInputEl.files[0] : null;
        this.file = file;
        if (file) {
            this.fileNameEl.textContent = file.name;
            this.fileNameEl.setAttribute("style", "display:block;");
        } else {
            this.fileNameEl.textContent = "";
            this.fileNameEl.setAttribute("style", "display:none;");
        }
        });
    }
    
    private updateSubmitState() {
        const valid = this.formEl.checkValidity();
        this.submitBtnEl.disabled = !valid || this.isSaving;
    }
    
    private showFormView() {
        this.bodyFormEl.style.display = "";
        this.bodyResultEl.style.display = "none";
        this.errorEl.style.display = "none";
        this.successEl.style.display = "none";
    }
    
    private showResultView(hasErrors: boolean) {
        this.bodyFormEl.style.display = "none";
        this.bodyResultEl.style.display = "";
        this.errorEl.style.display = hasErrors ? "" : "none";
        this.successEl.style.display = hasErrors ? "none" : "";
    }
    
    private async readFileAsDataURL(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.onloadend = () => resolve(String(reader.result));
        reader.readAsDataURL(file);
        });
    }
    
    private async sendEmailToAuthor(): Promise<void> {
        if (this.isSaving) return;
        
        this.isSaving = true;
        this.updateSubmitState();
        
        try {
            const payload: EmailData = {
                ...this.emailData,
                message: this.messageEl.value.trim(),
                file: null,
                fileName: "",
            };
        
            if (this.file) {
                payload.file = await this.readFileAsDataURL(this.file);
                payload.fileName = this.file.name;
            }
        
            await ContactWithAuthorService.sendToAuthor(payload);
        
            this.formSubmitted = true;
            this.hasErrors = false;
            this.showResultView(false);
        } catch (err: any) {
            this.formSubmitted = true;
            this.hasErrors = true;
            this.showResultView(true);
        } finally {
            this.isSaving = false;
            this.updateSubmitState();
        }
    }
}

export const initializeContactWithAuthor = () => {
    document.querySelectorAll<HTMLElement>(".js-contact-with-author").forEach((root) => {
        try {
        new ContactWithAuthorController(root);
        } catch (e) {
            console.error("Failed to init ContactWithAuthorController", e);
        }
    });
}